import { useState, useEffect } from 'react'
import { adminApi } from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import { formatCurrency, formatDate } from '../../config'
import type { Registration, Event } from '../../types'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import SilentAuctionModal from '../../components/common/SilentAuctionModal'
import { formatPhoneNumber, lowercaseEmail } from '../../utils/formatters'

interface PaidGolfer {
  name: string
  email: string
  source: 'registration' | 'sponsor_foursome'
  teamName?: string
  sponsorName?: string
}

export default function AdminRegistrations() {
  const { getToken } = useAuth()
  const [registrations, setRegistrations] = useState<Registration[]>([])
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedEventId, setSelectedEventId] = useState<string>('')
  const [selectedRegistration, setSelectedRegistration] =
    useState<Registration | null>(null)
  const [processingRefund, setProcessingRefund] = useState(false)
  const [sendingInvite, setSendingInvite] = useState(false)
  const [sendingReminder, setSendingReminder] = useState(false)
  const [sendingBulkReminder, setSendingBulkReminder] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedForReminder, setSelectedForReminder] = useState<Set<string>>(new Set())
  const [showBulkReminderModal, setShowBulkReminderModal] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [bulkReminderMessage, setBulkReminderMessage] = useState('')
  const [bulkReminderSendTo, setBulkReminderSendTo] = useState<'primary' | 'golfers' | 'all'>('primary')
  const [selectedForWaitlistInvite, setSelectedForWaitlistInvite] = useState<Set<string>>(new Set())
  const [showBulkWaitlistModal, setShowBulkWaitlistModal] = useState(false)
  const [sendingBulkWaitlistInvite, setSendingBulkWaitlistInvite] = useState(false)
  const [sendingUnpaidReminder, setSendingUnpaidReminder] = useState(false)
  const [showBulkUnpaidModal, setShowBulkUnpaidModal] = useState(false)
  const [selectedForUnpaidReminder, setSelectedForUnpaidReminder] = useState<Set<string>>(new Set())
  const [showManualRegModal, setShowManualRegModal] = useState(false)
  const [auctionModalOpen, setAuctionModalOpen] = useState(false)
  const [auctionGolfers, setAuctionGolfers] = useState<PaidGolfer[]>([])
  const [loadingAuctionGolfers, setLoadingAuctionGolfers] = useState(false)
  const [auctionVariant, setAuctionVariant] = useState<'reminder' | 'final-hour'>('reminder')
  const [submittingManualReg, setSubmittingManualReg] = useState(false)
  const [manualRegForm, setManualRegForm] = useState({
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    golfersCount: 1,
    golfers: [{ name: '', email: '', phone: '' }] as Array<{ name: string; email: string; phone: string }>,
    teamName: '',
    amountPaidDollars: '',
  })

  const fetchData = async () => {
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const [regsData, eventsData] = await Promise.all([
        adminApi.getRegistrations(token, selectedEventId || undefined),
        adminApi.getEvents(token),
      ])

      setRegistrations(regsData.registrations)
      setEvents(eventsData.events)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [getToken, selectedEventId])

  const getRefundAmount = (registration: Registration) => {
    const golferCount = registration.paidGolfersCount || registration.golfersCount || 0
    const fee = golferCount * 500 // $5 per golfer
    return Math.max(0, registration.amountTotalCents - fee)
  }

  const handleRefund = async (registration: Registration) => {
    const refundAmount = getRefundAmount(registration)
    const golferCount = registration.paidGolfersCount || registration.golfersCount || 0
    const fee = golferCount * 500
    if (
      !confirm(
        `Are you sure you want to refund ${formatCurrency(refundAmount)} to ${registration.primaryContact.name}?\n\nOriginal amount: ${formatCurrency(registration.amountTotalCents)}\nProcessing fee: ${formatCurrency(fee)} ($5 x ${golferCount} golfer${golferCount !== 1 ? 's' : ''})\nRefund amount: ${formatCurrency(refundAmount)}`
      )
    ) {
      return
    }

    setProcessingRefund(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      await adminApi.processRefund(token, registration.registrationId)
      alert('Refund processed successfully')
      setSelectedRegistration(null)
      fetchData()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to process refund')
    } finally {
      setProcessingRefund(false)
    }
  }

  const handleSendWaitlistInvite = async (registration: Registration) => {
    const inviteNumber = (registration.waitlistInvitesSent || 0) + 1
    const inviteLabels = ['first', 'second', 'third (final)']

    if (
      !confirm(
        `Send ${inviteLabels[inviteNumber - 1]} invite email to ${registration.primaryContact.name} (${registration.primaryContact.email})?`
      )
    ) {
      return
    }

    setSendingInvite(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const result = await adminApi.sendWaitlistInvite(token, registration.registrationId)
      alert(result.message)
      fetchData()
      // Update the selected registration with the new data
      setSelectedRegistration(result.registration)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to send invite')
    } finally {
      setSendingInvite(false)
    }
  }

  const handleSendReminder = async (registration: Registration, sendTo: 'primary' | 'golfers' | 'all' = 'primary', message?: string) => {
    const labels = {
      primary: `Send event reminder to ${registration.primaryContact.name} (${registration.primaryContact.email})?`,
      golfers: 'Send event reminder to all golfers with email addresses?',
      all: 'Send event reminder to primary contact and all golfers?',
    }
    if (!confirm(labels[sendTo])) {
      return
    }

    setSendingReminder(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const result = await adminApi.sendReminder(token, registration.registrationId, message, sendTo)
      alert(result.message)
      fetchData()
      setSelectedRegistration(result.registration)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to send reminder')
    } finally {
      setSendingReminder(false)
    }
  }

  const handleBulkReminder = async () => {
    if (selectedForReminder.size === 0) {
      alert('Please select at least one registration')
      return
    }

    // In golfers mode, keys are "regId:golferIdx"; otherwise plain regIds
    const isGolfersMode = bulkReminderSendTo === 'golfers'
    let registrationIds: string[]
    let selectedGolfers: Record<string, number[]> | undefined

    if (isGolfersMode) {
      // Group selected golfer indices by registration ID
      const golferMap: Record<string, number[]> = {}
      for (const key of selectedForReminder) {
        const [regId, idxStr] = key.split(':')
        if (!golferMap[regId]) golferMap[regId] = []
        golferMap[regId].push(parseInt(idxStr, 10))
      }
      registrationIds = Object.keys(golferMap)
      selectedGolfers = golferMap
    } else {
      registrationIds = Array.from(selectedForReminder)
    }

    const count = isGolfersMode ? selectedForReminder.size : registrationIds.length
    if (
      !confirm(
        `Send event reminder email to ${count} ${isGolfersMode ? 'golfer(s)' : 'registrant(s)'}?`
      )
    ) {
      return
    }

    setSendingBulkReminder(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const result = await adminApi.sendBulkReminder(
        token,
        registrationIds,
        bulkReminderMessage || undefined,
        bulkReminderSendTo,
        selectedGolfers
      )
      alert(result.message)
      setShowBulkReminderModal(false)
      setBulkReminderMessage('')
      setBulkReminderSendTo('primary')
      setSelectedForReminder(new Set())
      fetchData()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to send bulk reminders')
    } finally {
      setSendingBulkReminder(false)
    }
  }

  const toggleReminderSelection = (registrationId: string) => {
    const newSet = new Set(selectedForReminder)
    if (newSet.has(registrationId)) {
      newSet.delete(registrationId)
    } else {
      newSet.add(registrationId)
    }
    setSelectedForReminder(newSet)
  }

  // Check if registration has PAID golfers with emails different from primary contact
  const hasDistinctGolferEmails = (r: Registration) => {
    const primaryEmail = (r.primaryContact.email || '').toLowerCase().trim()
    // Get emails of unpaid golfers (pending status)
    const unpaidEmails = new Set(
      (r.unpaidGolfers || [])
        .filter(ug => ug.status === 'pending')
        .map(ug => (ug.email || '').toLowerCase().trim())
    )
    return (r.golfers || []).some(g => {
      const golferEmail = (g.email || '').toLowerCase().trim()
      return golferEmail && golferEmail !== primaryEmail && !unpaidEmails.has(golferEmail)
    })
  }

  const isEligibleForReminder = (r: Registration, sendTo: 'primary' | 'golfers' | 'all') => {
    if (r.status !== 'paid') return false
    if (sendTo === 'primary') return !r.eventReminderSent
    if (sendTo === 'golfers') return hasDistinctGolferEmails(r) && !r.eventGolferRemindersSent
    return !r.eventReminderSent || (hasDistinctGolferEmails(r) && !r.eventGolferRemindersSent)
  }

  const selectAllEligibleForReminder = () => {
    const eligible = registrations.filter((r) => isEligibleForReminder(r, bulkReminderSendTo))
    if (bulkReminderSendTo === 'golfers') {
      // Select individual golfers with composite keys (only those with distinct emails and paid)
      const keys = new Set<string>()
      eligible.forEach((r) => {
        const primaryEmail = (r.primaryContact.email || '').toLowerCase().trim()
        // Get emails of unpaid golfers (pending status)
        const unpaidEmails = new Set(
          (r.unpaidGolfers || [])
            .filter(ug => ug.status === 'pending')
            .map(ug => (ug.email || '').toLowerCase().trim())
        )
        ;(r.golfers || []).forEach((g, i) => {
          const golferEmail = (g.email || '').toLowerCase().trim()
          if (g.name && golferEmail && golferEmail !== primaryEmail && !unpaidEmails.has(golferEmail)) {
            keys.add(`${r.registrationId}:${i}`)
          }
        })
      })
      setSelectedForReminder(keys)
    } else {
      setSelectedForReminder(new Set(eligible.map((r) => r.registrationId)))
    }
  }

  const clearReminderSelection = () => {
    setSelectedForReminder(new Set())
  }

  // Count of eligible recipients for reminders
  const eligibleForReminderCount = (() => {
    if (bulkReminderSendTo === 'golfers') {
      return registrations
        .filter((r) => isEligibleForReminder(r, 'golfers'))
        .reduce((count, r) => {
          const primaryEmail = (r.primaryContact.email || '').toLowerCase().trim()
          // Get emails of unpaid golfers (pending status)
          const unpaidEmails = new Set(
            (r.unpaidGolfers || [])
              .filter(ug => ug.status === 'pending')
              .map(ug => (ug.email || '').toLowerCase().trim())
          )
          const golfers = (r.golfers || []).filter(g => {
            const golferEmail = (g.email || '').toLowerCase().trim()
            return g.name && golferEmail && golferEmail !== primaryEmail && !unpaidEmails.has(golferEmail)
          })
          return count + golfers.length
        }, 0)
    } else if (bulkReminderSendTo === 'all') {
      // Count primary contacts + distinct golfers (excluding unpaid)
      return registrations
        .filter((r) => isEligibleForReminder(r, 'all'))
        .reduce((count, r) => {
          const primaryEmail = (r.primaryContact.email || '').toLowerCase().trim()
          // Get emails of unpaid golfers (pending status)
          const unpaidEmails = new Set(
            (r.unpaidGolfers || [])
              .filter(ug => ug.status === 'pending')
              .map(ug => (ug.email || '').toLowerCase().trim())
          )
          const distinctGolferCount = (r.golfers || []).filter(g => {
            const golferEmail = (g.email || '').toLowerCase().trim()
            return g.name && golferEmail && golferEmail !== primaryEmail && !unpaidEmails.has(golferEmail)
          }).length
          return count + 1 + distinctGolferCount // 1 for primary + golfers
        }, 0)
    } else {
      return registrations.filter((r) => isEligibleForReminder(r, bulkReminderSendTo)).length
    }
  })()

  // Count of eligible registrations for waitlist invites
  const eligibleForWaitlistInviteCount = registrations.filter(
    (r) => (r.status === 'waitlist' || r.status === 'waitlist_invited') && (r.waitlistInvitesSent || 0) < 3
  ).length

  // Get all unpaid golfers eligible for payment reminders (remindersSent < 4)
  const getEligibleUnpaidGolfers = () => {
    const result: Array<{
      registrationId: string
      golferIndex: number
      name: string
      email: string
      remindersSent: number
      primaryContactName: string
    }> = []

    registrations.forEach((r) => {
      if (r.status === 'paid' && r.unpaidGolfers) {
        r.unpaidGolfers.forEach((ug) => {
          if (ug.status === 'pending' && ug.remindersSent < 4) {
            result.push({
              registrationId: r.registrationId,
              golferIndex: ug.golferIndex,
              name: ug.name,
              email: ug.email,
              remindersSent: ug.remindersSent,
              primaryContactName: r.primaryContact.name,
            })
          }
        })
      }
    })
    return result
  }

  const eligibleUnpaidGolfers = getEligibleUnpaidGolfers()
  const eligibleForUnpaidReminderCount = eligibleUnpaidGolfers.length

  const selectAllEligibleUnpaid = () => {
    const keys = new Set(eligibleUnpaidGolfers.map((ug) => `${ug.registrationId}:${ug.golferIndex}`))
    setSelectedForUnpaidReminder(keys)
  }

  const clearUnpaidSelection = () => {
    setSelectedForUnpaidReminder(new Set())
  }

  const handleBulkUnpaidReminder = async () => {
    if (selectedForUnpaidReminder.size === 0) {
      alert('Please select at least one unpaid golfer')
      return
    }

    if (!confirm(`Send payment reminders to ${selectedForUnpaidReminder.size} unpaid golfer(s)?`)) {
      return
    }

    setSendingUnpaidReminder(true)
    let successCount = 0
    let failCount = 0

    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      for (const key of selectedForUnpaidReminder) {
        const [registrationId, golferIndexStr] = key.split(':')
        const golferIndex = parseInt(golferIndexStr, 10)
        try {
          await adminApi.sendUnpaidGolferReminder(token, registrationId, golferIndex)
          successCount++
        } catch {
          failCount++
        }
      }

      alert(`Sent ${successCount} reminder(s)${failCount > 0 ? `, ${failCount} failed` : ''}`)
      setShowBulkUnpaidModal(false)
      setSelectedForUnpaidReminder(new Set())
      fetchData()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to send reminders')
    } finally {
      setSendingUnpaidReminder(false)
    }
  }

  const handleBulkWaitlistInvite = async () => {
    if (selectedForWaitlistInvite.size === 0) {
      alert('Please select at least one waitlist registration')
      return
    }

    if (
      !confirm(
        `Send waitlist invite emails to ${selectedForWaitlistInvite.size} registrant(s)?`
      )
    ) {
      return
    }

    setSendingBulkWaitlistInvite(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const result = await adminApi.sendBulkWaitlistInvite(
        token,
        Array.from(selectedForWaitlistInvite)
      )
      alert(result.message)
      setShowBulkWaitlistModal(false)
      setSelectedForWaitlistInvite(new Set())
      fetchData()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to send bulk waitlist invites')
    } finally {
      setSendingBulkWaitlistInvite(false)
    }
  }

  const resetManualRegForm = () => {
    setManualRegForm({
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      golfersCount: 1,
      golfers: [{ name: '', email: '', phone: '' }],
      teamName: '',
      amountPaidDollars: '',
    })
  }

  const handleManualRegGolfersCountChange = (count: number) => {
    const golfers = [...manualRegForm.golfers]
    while (golfers.length < count) golfers.push({ name: '', email: '', phone: '' })
    setManualRegForm({ ...manualRegForm, golfersCount: count, golfers: golfers.slice(0, count) })
  }

  const handleManualRegGolferChange = (index: number, field: string, value: string) => {
    const golfers = [...manualRegForm.golfers]
    golfers[index] = { ...golfers[index], [field]: value }
    setManualRegForm({ ...manualRegForm, golfers })
  }

  const handleCreateManualRegistration = async () => {
    if (!selectedEventId) {
      alert('Please select an event first')
      return
    }
    if (!manualRegForm.contactName || !manualRegForm.contactEmail) {
      alert('Contact name and email are required')
      return
    }
    // Validate golfer emails: required if name is entered, must be valid format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    for (let i = 0; i < manualRegForm.golfersCount; i++) {
      const g = manualRegForm.golfers[i]
      if (g.name && !g.email) {
        alert(`Golfer ${i + 1}: Email is required when a name is entered`)
        return
      }
      if (g.email && !emailRegex.test(g.email)) {
        alert(`Golfer ${i + 1}: Please enter a valid email address`)
        return
      }
    }

    const amountCents = Math.round(parseFloat(manualRegForm.amountPaidDollars || '0') * 100)
    if (amountCents <= 0) {
      alert('Please enter a valid amount')
      return
    }

    setSubmittingManualReg(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const result = await adminApi.createManualRegistration(token, {
        eventId: selectedEventId,
        primaryContact: {
          name: manualRegForm.contactName,
          email: manualRegForm.contactEmail,
          phone: manualRegForm.contactPhone,
        },
        golfersCount: manualRegForm.golfersCount,
        golfers: manualRegForm.golfers,
        teamName: manualRegForm.teamName || undefined,
        amountPaidCents: amountCents,
      })
      alert(result.message)
      setShowManualRegModal(false)
      resetManualRegForm()
      fetchData()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create registration')
    } finally {
      setSubmittingManualReg(false)
    }
  }

  const handleSendUnpaidReminder = async (registration: Registration, golferIndex?: number) => {
    const target = golferIndex !== undefined
      ? `golfer #${golferIndex + 1}`
      : 'all unpaid golfers'
    if (!confirm(`Send reminder to ${target}?`)) return

    setSendingUnpaidReminder(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      const result = await adminApi.sendUnpaidGolferReminder(token, registration.registrationId, golferIndex)
      alert(result.message)
      fetchData()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to send reminder')
    } finally {
      setSendingUnpaidReminder(false)
    }
  }

  const toggleWaitlistInviteSelection = (registrationId: string) => {
    const newSet = new Set(selectedForWaitlistInvite)
    if (newSet.has(registrationId)) {
      newSet.delete(registrationId)
    } else {
      newSet.add(registrationId)
    }
    setSelectedForWaitlistInvite(newSet)
  }

  const selectAllEligibleForWaitlistInvite = () => {
    const eligible = registrations.filter(
      (r) => (r.status === 'waitlist' || r.status === 'waitlist_invited') && (r.waitlistInvitesSent || 0) < 3
    )
    setSelectedForWaitlistInvite(new Set(eligible.map((r) => r.registrationId)))
  }

  const clearWaitlistInviteSelection = () => {
    setSelectedForWaitlistInvite(new Set())
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'refunded':
        return 'bg-red-100 text-red-800'
      case 'cancelled':
        return 'bg-gray-100 text-gray-600'
      case 'waitlist':
        return 'bg-orange-100 text-orange-800'
      case 'waitlist_invited':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-600'
    }
  }

  // Filter registrations based on status and search filters
  const filteredRegistrations = registrations.filter((reg) => {
    // Status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'waitlist') {
        if (reg.status !== 'waitlist' && reg.status !== 'waitlist_invited') return false
      } else if (statusFilter === 'check') {
        if (reg.paymentMethod !== 'check') return false
      } else if (statusFilter === 'sponsorship') {
        if (reg.paymentMethod !== 'sponsorship') return false
      } else if (statusFilter === 'unpaid_golfers') {
        if (reg.status !== 'paid' || !reg.unpaidGolfers?.some(ug => ug.status === 'pending')) return false
      } else if (reg.status !== statusFilter) {
        return false
      }
    }
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      const name = reg.primaryContact.name.toLowerCase()
      const email = reg.primaryContact.email.toLowerCase()
      const phone = reg.primaryContact.phone?.toLowerCase() || ''
      const teamName = (reg.teamName || '').toLowerCase()
      // Also search in golfer names
      const golferNames = reg.golfers?.map(g => g.name.toLowerCase()).join(' ') || ''
      const golferEmails = reg.golfers?.map(g => g.email.toLowerCase()).join(' ') || ''
      if (!name.includes(query) && !email.includes(query) && !phone.includes(query) &&
          !teamName.includes(query) && !golferNames.includes(query) && !golferEmails.includes(query)) {
        return false
      }
    }
    return true
  })

  const handleOpenAuctionModal = async (variant: 'reminder' | 'final-hour' = 'reminder') => {
    setAuctionVariant(variant)
    setAuctionModalOpen(true)
    setAuctionGolfers([])
    setLoadingAuctionGolfers(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      const data = await adminApi.getPaidGolfers(token)
      setAuctionGolfers(data.golfers)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load golfers')
      setAuctionModalOpen(false)
    } finally {
      setLoadingAuctionGolfers(false)
    }
  }

  const handleExport = async () => {
    setExporting(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      const blob = await adminApi.exportRegistrations(token, selectedEventId || undefined)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `registrations-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to export')
    } finally {
      setExporting(false)
    }
  }

  if (loading) {
    return (
      <div className="py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={fetchData} className="btn btn-primary">
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Registrations</h1>

        <div className="flex gap-3">
          <select
            className="input w-auto"
            value={selectedEventId}
            onChange={(e) => setSelectedEventId(e.target.value)}
          >
            <option value="">All Events</option>
            {events.map((event) => (
              <option key={event.eventId} value={event.eventId}>
                {event.name}
              </option>
            ))}
          </select>

          <button
            onClick={() => {
              if (!selectedEventId) {
                alert('Please select an event first')
                return
              }
              resetManualRegForm()
              setShowManualRegModal(true)
            }}
            className="btn btn-primary"
          >
            + Manual Registration
          </button>

          <button
            onClick={handleExport}
            disabled={exporting}
            className="btn btn-secondary"
          >
            {exporting ? (
              <span className="flex items-center gap-2">
                <LoadingSpinner size="sm" />
                Exporting...
              </span>
            ) : (
              'Export CSV'
            )}
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="card mb-6">
        <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-4 text-center">
          {/* 1. Paid Golfers */}
          <div
            className={`cursor-pointer p-2 rounded ${statusFilter === 'paid' ? 'bg-green-50 ring-2 ring-green-500' : 'hover:bg-gray-50'}`}
            onClick={() => setStatusFilter(statusFilter === 'paid' ? 'all' : 'paid')}
          >
            <p className="text-2xl font-bold text-green-600">
              {registrations.filter((r) => r.status === 'paid').reduce((sum, r) => sum + (r.paidGolfersCount || 0), 0)}
            </p>
            <p className="text-sm text-gray-500">Paid Golfers</p>
          </div>
          {/* 2. Pending Golfers */}
          <div
            className={`cursor-pointer p-2 rounded ${statusFilter === 'unpaid_golfers' ? 'bg-amber-50 ring-2 ring-amber-500' : 'hover:bg-gray-50'}`}
            onClick={() => setStatusFilter(statusFilter === 'unpaid_golfers' ? 'all' : 'unpaid_golfers')}
          >
            <p className="text-2xl font-bold text-amber-600">
              {registrations.reduce((sum, r) => sum + (r.unpaidGolfers?.filter(ug => ug.status === 'pending').length || 0), 0)}
            </p>
            <p className="text-sm text-gray-500">Pending Golfers</p>
          </div>
          {/* 3. Sponsorship Golfers */}
          <div
            className={`cursor-pointer p-2 rounded ${statusFilter === 'sponsorship' ? 'bg-teal-50 ring-2 ring-teal-500' : 'hover:bg-gray-50'}`}
            onClick={() => setStatusFilter(statusFilter === 'sponsorship' ? 'all' : 'sponsorship')}
          >
            <p className="text-2xl font-bold text-teal-600">
              {registrations.filter((r) => r.paymentMethod === 'sponsorship').reduce((sum, r) => sum + (r.paidGolfersCount || r.golfersCount || 0), 0)}
            </p>
            <p className="text-sm text-gray-500">Sponsorship Golfers</p>
          </div>
          {/* 4. Total Golfers */}
          <div className="p-2">
            <p className="text-2xl font-bold text-gray-800">
              {registrations.filter((r) => r.status === 'paid').reduce((sum, r) => sum + r.golfersCount, 0)}
            </p>
            <p className="text-sm text-gray-500">Total Golfers</p>
          </div>
          {/* 5. Waitlist */}
          <div
            className={`cursor-pointer p-2 rounded ${statusFilter === 'waitlist' ? 'bg-orange-50 ring-2 ring-orange-500' : 'hover:bg-gray-50'}`}
            onClick={() => setStatusFilter(statusFilter === 'waitlist' ? 'all' : 'waitlist')}
          >
            <p className="text-2xl font-bold text-orange-600">
              {registrations.filter((r) => r.status === 'waitlist' || r.status === 'waitlist_invited').length}
            </p>
            <p className="text-sm text-gray-500">Waitlist</p>
          </div>
          {/* 6. Refunded */}
          <div
            className={`cursor-pointer p-2 rounded ${statusFilter === 'refunded' ? 'bg-red-50 ring-2 ring-red-500' : 'hover:bg-gray-50'}`}
            onClick={() => setStatusFilter(statusFilter === 'refunded' ? 'all' : 'refunded')}
          >
            <p className="text-2xl font-bold text-red-600">
              {registrations.filter((r) => r.status === 'refunded').length}
            </p>
            <p className="text-sm text-gray-500">Refunded</p>
          </div>
          {/* 7. Check */}
          <div
            className={`cursor-pointer p-2 rounded ${statusFilter === 'check' ? 'bg-purple-50 ring-2 ring-purple-500' : 'hover:bg-gray-50'}`}
            onClick={() => setStatusFilter(statusFilter === 'check' ? 'all' : 'check')}
          >
            <p className="text-2xl font-bold text-purple-600">
              {registrations.filter((r) => r.paymentMethod === 'check').length}
            </p>
            <p className="text-sm text-gray-500">Check</p>
          </div>
          {/* 8. Collected */}
          <div className="p-2">
            <p className="text-2xl font-bold text-sla-green">
              {formatCurrency(
                registrations
                  .filter((r) => r.status === 'paid')
                  .reduce((sum, r) => sum + r.amountTotalCents, 0)
              )}
            </p>
            <p className="text-sm text-gray-500">Collected</p>
          </div>
          {/* 9. Incomplete Payment */}
          <div
            className={`cursor-pointer p-2 rounded ${statusFilter === 'pending' ? 'bg-yellow-50 ring-2 ring-yellow-500' : 'hover:bg-gray-50'}`}
            onClick={() => setStatusFilter(statusFilter === 'pending' ? 'all' : 'pending')}
          >
            <p className="text-2xl font-bold text-yellow-600">
              {registrations.filter((r) => r.status === 'pending').length}
            </p>
            <p className="text-sm text-gray-500">Incomplete Payment</p>
          </div>
        </div>
        {statusFilter !== 'all' && (
          <div className="mt-4 text-center">
            <button
              onClick={() => setStatusFilter('all')}
              className="text-sm text-sla-blue hover:underline"
            >
              Clear filter - Show all registrations
            </button>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="card mb-6">
        <input
          type="text"
          className="input"
          placeholder="Search by name, email, phone, team name, or golfer info..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <div className="mt-2 text-sm text-gray-500">
            Found {filteredRegistrations.length} registration{filteredRegistrations.length !== 1 ? 's' : ''} matching "{searchQuery}"
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      <div className="card mb-6">
        <h3 className="font-semibold text-gray-700 mb-3">Bulk Actions</h3>
        <div className="flex flex-wrap gap-4">
          {/* Send Event Reminders to Paid */}
          <button
            onClick={() => setShowBulkReminderModal(true)}
            disabled={eligibleForReminderCount === 0}
            className="btn btn-secondary text-sm"
          >
            Send Upcoming Event Reminders ({eligibleForReminderCount} eligible)
          </button>

          {/* Send Pending Payment Reminders */}
          <button
            onClick={() => {
              selectAllEligibleUnpaid()
              setShowBulkUnpaidModal(true)
            }}
            disabled={eligibleForUnpaidReminderCount === 0}
            className="btn btn-secondary text-sm"
          >
            Send Pending Payment Reminders ({eligibleForUnpaidReminderCount} eligible)
          </button>

          {/* Send Waitlist Invites */}
          <button
            onClick={() => setShowBulkWaitlistModal(true)}
            disabled={eligibleForWaitlistInviteCount === 0}
            className="btn btn-secondary text-sm"
          >
            Send Waitlist Invites ({eligibleForWaitlistInviteCount} eligible)
          </button>

          {/* Send Silent Auction Email */}
          <button
            onClick={() => handleOpenAuctionModal('reminder')}
            className="btn bg-amber-600 hover:bg-amber-700 text-white text-sm"
          >
            Send Silent Auction Email
          </button>

          {/* Send Final Hour Auction Reminder */}
          <button
            onClick={() => handleOpenAuctionModal('final-hour')}
            className="btn bg-orange-600 hover:bg-orange-700 text-white text-sm"
          >
            Send Final Hour Auction Reminder
          </button>
        </div>
      </div>

      {/* Bulk Reminder Modal */}
      {showBulkReminderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-800">
                  Send Event Reminders
                </h2>
                <button
                  onClick={() => {
                    setShowBulkReminderModal(false)
                    setBulkReminderMessage('')
                    setBulkReminderSendTo('primary')
                    setSelectedForReminder(new Set())
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className="text-gray-600 mb-4">
                Send event reminder emails to paid registrations.
              </p>

              <div className="mb-4">
                <label className="label">Send To</label>
                <select
                  className="input"
                  value={bulkReminderSendTo}
                  onChange={(e) => {
                    setBulkReminderSendTo(e.target.value as 'primary' | 'golfers' | 'all')
                    setSelectedForReminder(new Set())
                  }}
                >
                  <option value="primary">Primary Contact Only</option>
                  <option value="golfers">Golfers Only (with different email from primary)</option>
                  <option value="all">Everyone (Primary + Golfers)</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="label">Custom Message (optional)</label>
                <textarea
                  className="input"
                  rows={3}
                  placeholder="Add a custom message to include in the reminder email..."
                  value={bulkReminderMessage}
                  onChange={(e) => setBulkReminderMessage(e.target.value)}
                />
              </div>

              <div className="mb-4 flex gap-2">
                <button
                  onClick={selectAllEligibleForReminder}
                  className="text-sm text-sla-blue hover:underline"
                >
                  Select all eligible ({eligibleForReminderCount})
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={clearReminderSelection}
                  className="text-sm text-gray-500 hover:underline"
                >
                  Clear selection
                </button>
              </div>

              <div className="border rounded-lg max-h-60 overflow-y-auto mb-4">
                {bulkReminderSendTo === 'golfers' ? (
                  // Show individual golfers with emails different from primary contact
                  registrations
                    .filter((r) => r.status === 'paid' && hasDistinctGolferEmails(r))
                    .map((reg) => {
                      const alreadySent = !!reg.eventGolferRemindersSent
                      const primaryEmail = (reg.primaryContact.email || '').toLowerCase().trim()
                      // Get emails of unpaid golfers (pending status)
                      const unpaidEmails = new Set(
                        (reg.unpaidGolfers || [])
                          .filter(ug => ug.status === 'pending')
                          .map(ug => (ug.email || '').toLowerCase().trim())
                      )
                      // Only show golfers with distinct emails from primary AND who have paid
                      const golfers = (reg.golfers || []).filter(g => {
                        const golferEmail = (g.email || '').toLowerCase().trim()
                        return g.name && golferEmail && golferEmail !== primaryEmail && !unpaidEmails.has(golferEmail)
                      })
                      if (golfers.length === 0) return null
                      return (
                        <div key={reg.registrationId}>
                          <div className="px-3 pt-2 pb-1 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b">
                            {reg.primaryContact.name}'s Registration
                            {reg.paymentMethod === 'sponsorship' && (
                              <span className="ml-2 text-purple-600">(Sponsorship)</span>
                            )}
                          </div>
                          {golfers.map((g) => {
                            // Find the original index in the full golfers array
                            const originalIndex = (reg.golfers || []).findIndex(og => og.email === g.email && og.name === g.name)
                            return (
                              <label
                                key={`${reg.registrationId}-${originalIndex}`}
                                className={`flex items-center gap-3 p-3 border-b last:border-b-0 ${alreadySent ? 'bg-gray-100 opacity-60' : 'hover:bg-gray-50 cursor-pointer'}`}
                              >
                                <input
                                  type="checkbox"
                                  disabled={alreadySent}
                                  checked={selectedForReminder.has(`${reg.registrationId}:${originalIndex}`)}
                                  onChange={() => toggleReminderSelection(`${reg.registrationId}:${originalIndex}`)}
                                  className="w-4 h-4"
                                />
                                <div className="flex-1">
                                  <div className="font-medium">{g.name}</div>
                                  <div className="text-sm text-gray-500">{g.email}</div>
                                </div>
                                {alreadySent && (
                                  <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                                    Sent {reg.eventGolferRemindersSentAt ? formatDate(reg.eventGolferRemindersSentAt) : ''}
                                  </span>
                                )}
                              </label>
                            )
                          })}
                        </div>
                      )
                    })
                ) : (
                  // Show registrations by primary contact (with golfer info for 'all' mode)
                  registrations
                    .filter((r) => r.status === 'paid')
                    .map((reg) => {
                      const alreadySent = !isEligibleForReminder(reg, bulkReminderSendTo)
                      const sentDate = reg.eventReminderSentAt
                      const primaryEmail = (reg.primaryContact.email || '').toLowerCase().trim()
                      // Get emails of unpaid golfers (pending status) for 'all' mode
                      const unpaidEmailsAll = new Set(
                        (reg.unpaidGolfers || [])
                          .filter(ug => ug.status === 'pending')
                          .map(ug => (ug.email || '').toLowerCase().trim())
                      )
                      // Get golfers with distinct emails for 'all' mode display (exclude unpaid)
                      const distinctGolfers = bulkReminderSendTo === 'all'
                        ? (reg.golfers || []).filter(g => {
                            const golferEmail = (g.email || '').toLowerCase().trim()
                            return g.name && golferEmail && golferEmail !== primaryEmail && !unpaidEmailsAll.has(golferEmail)
                          })
                        : []
                      return (
                        <label
                          key={reg.registrationId}
                          className={`flex items-center gap-3 p-3 border-b last:border-b-0 ${alreadySent ? 'bg-gray-100 opacity-60' : 'hover:bg-gray-50 cursor-pointer'}`}
                        >
                          <input
                            type="checkbox"
                            disabled={alreadySent}
                            checked={selectedForReminder.has(reg.registrationId)}
                            onChange={() => toggleReminderSelection(reg.registrationId)}
                            className="w-4 h-4"
                          />
                          <div className="flex-1">
                            <div className="font-medium">
                              {reg.primaryContact.name}
                              {reg.paymentMethod === 'sponsorship' && (
                                <span className="ml-2 text-xs font-normal text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded">Sponsorship</span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{reg.primaryContact.email}</div>
                            {distinctGolfers.length > 0 && (
                              <div className="text-xs text-gray-400 mt-1">
                                + {distinctGolfers.length} golfer{distinctGolfers.length > 1 ? 's' : ''}: {distinctGolfers.map(g => g.name).join(', ')}
                              </div>
                            )}
                          </div>
                          {alreadySent && (
                            <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                              Sent {sentDate ? formatDate(sentDate) : ''}
                            </span>
                          )}
                        </label>
                      )
                    })
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowBulkReminderModal(false)
                    setBulkReminderMessage('')
                    setBulkReminderSendTo('primary')
                    setSelectedForReminder(new Set())
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkReminder}
                  disabled={sendingBulkReminder || selectedForReminder.size === 0}
                  className="btn btn-primary"
                >
                  {sendingBulkReminder ? (
                    <span className="flex items-center gap-2">
                      <LoadingSpinner size="sm" />
                      Sending...
                    </span>
                  ) : (
                    bulkReminderSendTo === 'golfers'
                      ? `Send to ${selectedForReminder.size} Golfer(s)`
                      : `Send to ${selectedForReminder.size} Registrant(s)`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Waitlist Invite Modal */}
      {showBulkWaitlistModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-800">
                  Send Waitlist Invites
                </h2>
                <button
                  onClick={() => {
                    setShowBulkWaitlistModal(false)
                    setSelectedForWaitlistInvite(new Set())
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className="text-gray-600 mb-4">
                Send invite emails to waitlist registrations with payment links. Each registrant can receive up to 3 invites.
              </p>

              <div className="mb-4 flex gap-2">
                <button
                  onClick={selectAllEligibleForWaitlistInvite}
                  className="text-sm text-sla-blue hover:underline"
                >
                  Select all eligible ({eligibleForWaitlistInviteCount})
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={clearWaitlistInviteSelection}
                  className="text-sm text-gray-500 hover:underline"
                >
                  Clear selection
                </button>
              </div>

              <div className="border rounded-lg max-h-60 overflow-y-auto mb-4">
                {registrations
                  .filter((r) => r.status === 'waitlist' || r.status === 'waitlist_invited')
                  .map((reg) => {
                    const invitesSent = reg.waitlistInvitesSent || 0
                    const maxReached = invitesSent >= 3
                    return (
                      <label
                        key={reg.registrationId}
                        className={`flex items-center gap-3 p-3 border-b last:border-b-0 ${maxReached ? 'bg-gray-100 opacity-60' : 'hover:bg-gray-50 cursor-pointer'}`}
                      >
                        <input
                          type="checkbox"
                          disabled={maxReached}
                          checked={selectedForWaitlistInvite.has(reg.registrationId)}
                          onChange={() => toggleWaitlistInviteSelection(reg.registrationId)}
                          className="w-4 h-4"
                        />
                        <div className="flex-1">
                          <div className="font-medium">{reg.primaryContact.name}</div>
                          <div className="text-sm text-gray-500">{reg.primaryContact.email}</div>
                          <div className="text-xs text-orange-600">
                            Position #{reg.waitlistPosition} | {invitesSent}/3 invites sent
                          </div>
                        </div>
                        {maxReached && (
                          <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                            Max invites sent
                          </span>
                        )}
                      </label>
                    )
                  })}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowBulkWaitlistModal(false)
                    setSelectedForWaitlistInvite(new Set())
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkWaitlistInvite}
                  disabled={sendingBulkWaitlistInvite || selectedForWaitlistInvite.size === 0}
                  className="btn btn-primary"
                >
                  {sendingBulkWaitlistInvite ? (
                    <span className="flex items-center gap-2">
                      <LoadingSpinner size="sm" />
                      Sending...
                    </span>
                  ) : (
                    `Send to ${selectedForWaitlistInvite.size} Registrant(s)`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Unpaid Payment Reminder Modal */}
      {showBulkUnpaidModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-800">
                  Send Pending Payment Reminders
                </h2>
                <button
                  onClick={() => {
                    setShowBulkUnpaidModal(false)
                    setSelectedForUnpaidReminder(new Set())
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className="text-gray-600 mb-4">
                Send payment reminder emails to unpaid golfers. Each golfer can receive up to 4 reminders.
              </p>

              <div className="mb-4 flex gap-2">
                <button
                  onClick={selectAllEligibleUnpaid}
                  className="text-sm text-sla-blue hover:underline"
                >
                  Select all eligible ({eligibleForUnpaidReminderCount})
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={clearUnpaidSelection}
                  className="text-sm text-gray-500 hover:underline"
                >
                  Clear selection
                </button>
              </div>

              <div className="border rounded-lg max-h-60 overflow-y-auto mb-4">
                {eligibleUnpaidGolfers.length === 0 ? (
                  <div className="p-4 text-gray-500 text-center">
                    No unpaid golfers eligible for reminders
                  </div>
                ) : (
                  eligibleUnpaidGolfers.map((ug) => {
                    const key = `${ug.registrationId}:${ug.golferIndex}`
                    return (
                      <label
                        key={key}
                        className="flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedForUnpaidReminder.has(key)}
                          onChange={() => {
                            const newSet = new Set(selectedForUnpaidReminder)
                            if (newSet.has(key)) {
                              newSet.delete(key)
                            } else {
                              newSet.add(key)
                            }
                            setSelectedForUnpaidReminder(newSet)
                          }}
                          className="w-4 h-4"
                        />
                        <div className="flex-1">
                          <div className="font-medium">{ug.name}</div>
                          <div className="text-sm text-gray-500">{ug.email}</div>
                          <div className="text-xs text-gray-400">
                            Registered by: {ug.primaryContactName}
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`text-xs px-2 py-1 rounded ${
                            ug.remindersSent === 0
                              ? 'bg-gray-100 text-gray-600'
                              : ug.remindersSent >= 3
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {ug.remindersSent}/4 sent
                          </span>
                        </div>
                      </label>
                    )
                  })
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowBulkUnpaidModal(false)
                    setSelectedForUnpaidReminder(new Set())
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkUnpaidReminder}
                  disabled={sendingUnpaidReminder || selectedForUnpaidReminder.size === 0}
                  className="btn btn-primary"
                >
                  {sendingUnpaidReminder ? (
                    <span className="flex items-center gap-2">
                      <LoadingSpinner size="sm" />
                      Sending...
                    </span>
                  ) : (
                    `Send to ${selectedForUnpaidReminder.size} Golfer(s)`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Registration Modal */}
      {showManualRegModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-800">
                  Manual Registration (Check Payment)
                </h2>
                <button
                  onClick={() => {
                    setShowManualRegModal(false)
                    resetManualRegForm()
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className="text-gray-600 mb-4">
                Create a registration for a golfer who paid by check. This will mark the registration as paid immediately.
              </p>

              {/* Primary Contact */}
              <div className="border rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-gray-700 mb-3">Primary Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="label">Name *</label>
                    <input
                      type="text"
                      className="input"
                      value={manualRegForm.contactName}
                      onChange={(e) => setManualRegForm({ ...manualRegForm, contactName: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="label">Email *</label>
                    <input
                      type="email"
                      className="input"
                      value={manualRegForm.contactEmail}
                      onChange={(e) => setManualRegForm({ ...manualRegForm, contactEmail: e.target.value })}
                      onBlur={(e) => setManualRegForm({ ...manualRegForm, contactEmail: lowercaseEmail(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className="label">Phone</label>
                    <input
                      type="tel"
                      className="input"
                      value={manualRegForm.contactPhone}
                      onChange={(e) => setManualRegForm({ ...manualRegForm, contactPhone: formatPhoneNumber(e.target.value) })}
                    />
                  </div>
                </div>
              </div>

              {/* Golfers */}
              <div className="border rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-700">Golfers</h3>
                  <select
                    className="input w-auto"
                    value={manualRegForm.golfersCount}
                    onChange={(e) => handleManualRegGolfersCountChange(parseInt(e.target.value))}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                      <option key={n} value={n}>
                        {n} golfer{n > 1 ? 's' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {manualRegForm.golfers.map((golfer, i) => (
                  <div key={i} className="mb-3 last:mb-0 pb-3 last:pb-0 border-b last:border-b-0">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-sm font-medium text-gray-600">Golfer {i + 1}</p>
                      {i === 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            const golfers = [...manualRegForm.golfers]
                            golfers[0] = {
                              name: manualRegForm.contactName,
                              email: manualRegForm.contactEmail,
                              phone: manualRegForm.contactPhone,
                            }
                            setManualRegForm({ ...manualRegForm, golfers })
                          }}
                          className="text-sm text-sla-blue hover:underline"
                        >
                          Copy from primary contact
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <input
                        type="text"
                        className="input"
                        placeholder="Name"
                        value={golfer.name}
                        onChange={(e) => handleManualRegGolferChange(i, 'name', e.target.value)}
                      />
                      <input
                        type="email"
                        className={`input ${golfer.name && !golfer.email ? 'border-red-400' : ''}`}
                        placeholder={golfer.name ? 'Email (required)' : 'Email'}
                        value={golfer.email}
                        onChange={(e) => handleManualRegGolferChange(i, 'email', e.target.value)}
                        onBlur={(e) => handleManualRegGolferChange(i, 'email', lowercaseEmail(e.target.value))}
                      />
                      <input
                        type="tel"
                        className="input"
                        placeholder="Phone"
                        value={golfer.phone}
                        onChange={(e) => handleManualRegGolferChange(i, 'phone', formatPhoneNumber(e.target.value))}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Team Name & Amount */}
              <div className="border rounded-lg p-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="label">Team Name (optional)</label>
                    <input
                      type="text"
                      className="input"
                      value={manualRegForm.teamName}
                      onChange={(e) => setManualRegForm({ ...manualRegForm, teamName: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="label">Amount Paid ($) *</label>
                    <input
                      type="number"
                      className="input"
                      placeholder="150.00"
                      min="0"
                      step="0.01"
                      value={manualRegForm.amountPaidDollars}
                      onChange={(e) => setManualRegForm({ ...manualRegForm, amountPaidDollars: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowManualRegModal(false)
                    resetManualRegForm()
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateManualRegistration}
                  disabled={submittingManualReg}
                  className="btn btn-primary"
                >
                  {submittingManualReg ? (
                    <span className="flex items-center gap-2">
                      <LoadingSpinner size="sm" />
                      Creating...
                    </span>
                  ) : (
                    'Create Registration'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Registrations Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                  Date
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">
                  Golfers
                </th>
                {/* Banquet column hidden - see docs/BANQUET_RESTORATION.md */}
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">
                  Amount
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">
                  Status
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredRegistrations.map((reg) => (
                <tr key={reg.registrationId} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">
                      {reg.primaryContact.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {reg.primaryContact.email}
                    </div>
                    {reg.waitlistPosition && (
                      <div className="text-xs text-orange-600">
                        Waitlist #{reg.waitlistPosition}
                        {reg.waitlistInvitesSent ? ` (${reg.waitlistInvitesSent} invite${reg.waitlistInvitesSent > 1 ? 's' : ''} sent)` : ''}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatDate(reg.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {reg.golfersCount > 0 && (
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                        {reg.paidGolfersCount !== undefined && reg.paidGolfersCount < reg.golfersCount
                          ? `${reg.paidGolfersCount}/${reg.golfersCount}`
                          : reg.golfersCount}
                      </span>
                    )}
                  </td>
                  {/* Banquet column hidden - see docs/BANQUET_RESTORATION.md */}
                  <td className="px-4 py-3 text-right font-medium">
                    {reg.status === 'waitlist' || reg.status === 'waitlist_invited'
                      ? <span className="text-gray-400">—</span>
                      : formatCurrency(reg.amountTotalCents)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(reg.status)}`}
                    >
                      {reg.status === 'waitlist_invited' ? 'invited' : reg.status}
                    </span>
                    {reg.paymentMethod === 'check' && (
                      <span className="ml-1 px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800">
                        Check
                      </span>
                    )}
                    {reg.paymentMethod === 'sponsorship' && (
                      <span className="ml-1 px-2 py-1 rounded text-xs font-medium bg-teal-100 text-teal-800">
                        Sponsorship
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => setSelectedRegistration(reg)}
                      className="text-sla-blue hover:underline text-sm"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredRegistrations.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {searchQuery
                ? 'No registrations match your search'
                : statusFilter !== 'all'
                ? 'No registrations match the selected filter'
                : 'No registrations found'}
            </div>
          )}
        </div>
      </div>

      {/* Registration Detail Modal */}
      {selectedRegistration && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-800">
                  Registration Details
                </h2>
                <button
                  onClick={() => setSelectedRegistration(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* Status */}
                <div className="flex items-center gap-2">
                  <span className="font-medium">Status:</span>
                  <span
                    className={`px-2 py-1 rounded text-sm font-medium ${getStatusBadgeClass(selectedRegistration.status)}`}
                  >
                    {selectedRegistration.status}
                  </span>
                  {selectedRegistration.paymentMethod === 'check' && (
                    <span className="px-2 py-1 rounded text-sm font-medium bg-purple-100 text-purple-800">
                      Check Payment
                    </span>
                  )}
                  {selectedRegistration.paymentMethod === 'sponsorship' && (
                    <span className="px-2 py-1 rounded text-sm font-medium bg-teal-100 text-teal-800">
                      Sponsorship Foursome
                    </span>
                  )}
                </div>

                {/* Primary Contact */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-700 mb-2">
                    Primary Contact
                  </h3>
                  <p>{selectedRegistration.primaryContact.name}</p>
                  <p className="text-sm text-gray-600">
                    {selectedRegistration.primaryContact.email}
                  </p>
                  <p className="text-sm text-gray-600">
                    {selectedRegistration.primaryContact.phone}
                  </p>
                </div>

                {/* Golfers */}
                {selectedRegistration.golfersCount > 0 && (
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold text-gray-700 mb-2">
                      Golfers ({selectedRegistration.golfersCount})
                    </h3>
                    {selectedRegistration.golfers.map((golfer, i) => {
                      if (!golfer.name && !golfer.email) return null
                      return (
                        <div key={i} className="mb-2 last:mb-0">
                          <p>{golfer.name || 'TBD'}</p>
                          <p className="text-sm text-gray-600">
                            {[golfer.email, golfer.phone].filter(Boolean).join(' | ') || 'No details'}
                          </p>
                        </div>
                      )
                    })}
                    {selectedRegistration.golfers.every(g => !g.name && !g.email) && (
                      <p className="text-sm text-gray-500 italic">No golfer details assigned yet</p>
                    )}
                  </div>
                )}

                {/* Team */}
                {selectedRegistration.teamName && (
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold text-gray-700 mb-2">
                      Team
                    </h3>
                    <p>
                      <strong>Team Name:</strong>{' '}
                      {selectedRegistration.teamName}
                    </p>
                  </div>
                )}

                {/* Referral Source */}
                {selectedRegistration.referralSource && (
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold text-gray-700 mb-2">Marketing Attribution</h3>
                    <p className="text-gray-600">
                      <strong>How they heard about us:</strong>{' '}
                      {selectedRegistration.referralSource === 'Other' && selectedRegistration.referralSourceOther
                        ? `Other - ${selectedRegistration.referralSourceOther}`
                        : selectedRegistration.referralSource}
                    </p>
                  </div>
                )}

                {/* Unpaid Golfers */}
                {selectedRegistration.status === 'paid' && selectedRegistration.unpaidGolfers && selectedRegistration.unpaidGolfers.length > 0 && (
                  <div className="border rounded-lg p-4 bg-orange-50">
                    <h3 className="font-semibold text-gray-700 mb-2">
                      Unpaid Golfers ({selectedRegistration.unpaidGolfers.filter(ug => ug.status === 'pending').length} pending)
                    </h3>
                    {selectedRegistration.unpaidGolfers.map((ug, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b last:border-b-0">
                        <div>
                          <p className="font-medium">{ug.name}</p>
                          <p className="text-sm text-gray-600">{ug.email}</p>
                          <p className="text-xs text-gray-500">
                            Status: {ug.status} | Reminders sent: {ug.remindersSent}
                          </p>
                        </div>
                        {ug.status === 'pending' && (
                          <button
                            onClick={() => handleSendUnpaidReminder(selectedRegistration, ug.golferIndex)}
                            disabled={sendingUnpaidReminder}
                            className="btn btn-secondary text-xs px-2 py-1"
                          >
                            Send Reminder
                          </button>
                        )}
                        {ug.status === 'registered' && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Registered</span>
                        )}
                      </div>
                    ))}
                    {selectedRegistration.unpaidGolfers.some(ug => ug.status === 'pending') && (
                      <button
                        onClick={() => handleSendUnpaidReminder(selectedRegistration)}
                        disabled={sendingUnpaidReminder}
                        className="btn btn-primary text-sm mt-3"
                      >
                        {sendingUnpaidReminder ? (
                          <span className="flex items-center gap-2">
                            <LoadingSpinner size="sm" />
                            Sending...
                          </span>
                        ) : (
                          'Send All Reminders'
                        )}
                      </button>
                    )}
                  </div>
                )}

                {/* Payment Info - hide for sponsorship registrations */}
                {selectedRegistration.paymentMethod !== 'sponsorship' && (
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h3 className="font-semibold text-gray-700 mb-2">
                      Payment Details
                    </h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span>Golf Registration:</span>
                      <span>
                        {formatCurrency(selectedRegistration.amountGolfCents)}
                      </span>
                      {/* Banquet hidden - show only if there was a banquet amount */}
                      {selectedRegistration.amountBanquetCents > 0 && (
                        <>
                          <span>Banquet Only (Legacy):</span>
                          <span>
                            {formatCurrency(selectedRegistration.amountBanquetCents)}
                          </span>
                        </>
                      )}
                      <span className="font-bold">Total:</span>
                      <span className="font-bold">
                        {formatCurrency(selectedRegistration.amountTotalCents)}
                      </span>
                    </div>
                    {selectedRegistration.paymentMethod === 'check' && (
                      <p className="text-xs text-purple-600 mt-2 font-medium">
                        Paid via check (manual registration)
                      </p>
                    )}
                    {selectedRegistration.stripePaymentIntentId && (
                      <p className="text-xs text-gray-500 mt-2">
                        Payment ID: {selectedRegistration.stripePaymentIntentId}
                      </p>
                    )}
                    {selectedRegistration.stripeRefundId && (
                      <p className="text-xs text-gray-500">
                        Refund ID: {selectedRegistration.stripeRefundId}
                      </p>
                    )}
                  </div>
                )}

                {/* Waitlist Info */}
                {(selectedRegistration.status === 'waitlist' || selectedRegistration.status === 'waitlist_invited') && (
                  <div className="border rounded-lg p-4 bg-orange-50">
                    <h3 className="font-semibold text-gray-700 mb-2">
                      Waitlist Information
                    </h3>
                    <p className="mb-2">
                      <strong>Position:</strong> #{selectedRegistration.waitlistPosition}
                    </p>
                    <p className="mb-2">
                      <strong>Invites Sent:</strong> {selectedRegistration.waitlistInvitesSent || 0} of 3
                    </p>
                    {selectedRegistration.waitlistInviteHistory && selectedRegistration.waitlistInviteHistory.length > 0 && (
                      <div className="mt-2">
                        <p className="font-medium text-sm mb-1">Invite History:</p>
                        <ul className="text-sm text-gray-600">
                          {selectedRegistration.waitlistInviteHistory.map((invite, i) => (
                            <li key={i}>
                              Invite #{invite.emailType} sent {formatDate(invite.sentAt)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Waitlist Send Invite Button */}
                {(selectedRegistration.status === 'waitlist' || selectedRegistration.status === 'waitlist_invited') &&
                  (selectedRegistration.waitlistInvitesSent || 0) < 3 && (
                  <div className="pt-4 border-t">
                    <button
                      onClick={() => handleSendWaitlistInvite(selectedRegistration)}
                      disabled={sendingInvite}
                      className="btn btn-primary"
                    >
                      {sendingInvite ? (
                        <span className="flex items-center gap-2">
                          <LoadingSpinner size="sm" />
                          Sending...
                        </span>
                      ) : (
                        `Send Invite #${(selectedRegistration.waitlistInvitesSent || 0) + 1} Email`
                      )}
                    </button>
                    <p className="text-sm text-gray-500 mt-2">
                      This will send an email with a payment link to complete registration.
                    </p>
                  </div>
                )}

                {/* Send Reminder Button (for paid registrations) */}
                {selectedRegistration.status === 'paid' && (
                  <div className="pt-4 border-t">
                    {selectedRegistration.paymentMethod === 'sponsorship' ? (
                      <>
                        <p className="text-sm font-medium text-gray-700 mb-2">Send Event Reminder</p>
                        <div className="flex flex-wrap gap-2 mb-2">
                          <button
                            onClick={() => handleSendReminder(selectedRegistration, 'primary')}
                            disabled={sendingReminder || selectedRegistration.eventReminderSent}
                            className={`btn text-sm ${selectedRegistration.eventReminderSent ? 'btn-secondary opacity-50' : 'btn-primary'}`}
                          >
                            {sendingReminder ? 'Sending...' : selectedRegistration.eventReminderSent ? 'Primary Sent' : 'Primary Contact'}
                          </button>
                          <button
                            onClick={() => handleSendReminder(selectedRegistration, 'golfers')}
                            disabled={sendingReminder || !!selectedRegistration.eventGolferRemindersSent}
                            className={`btn text-sm ${selectedRegistration.eventGolferRemindersSent ? 'btn-secondary opacity-50' : 'btn-primary'}`}
                          >
                            {sendingReminder ? 'Sending...' : selectedRegistration.eventGolferRemindersSent ? 'Golfers Sent' : 'Golfers Only'}
                          </button>
                          <button
                            onClick={() => handleSendReminder(selectedRegistration, 'all')}
                            disabled={sendingReminder || (selectedRegistration.eventReminderSent && !!selectedRegistration.eventGolferRemindersSent)}
                            className={`btn text-sm ${selectedRegistration.eventReminderSent && selectedRegistration.eventGolferRemindersSent ? 'btn-secondary opacity-50' : 'btn-primary'}`}
                          >
                            {sendingReminder ? 'Sending...' : 'Everyone'}
                          </button>
                        </div>
                        <p className="text-xs text-gray-500">
                          {selectedRegistration.eventReminderSent && (
                            <span>Primary sent {selectedRegistration.eventReminderSentAt ? formatDate(selectedRegistration.eventReminderSentAt) : ''}. </span>
                          )}
                          {selectedRegistration.eventGolferRemindersSent && (
                            <span>Golfers sent {selectedRegistration.eventGolferRemindersSentAt ? formatDate(selectedRegistration.eventGolferRemindersSentAt) : ''}.</span>
                          )}
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-4 mb-2">
                          <button
                            onClick={() => handleSendReminder(selectedRegistration, 'primary')}
                            disabled={sendingReminder || selectedRegistration.eventReminderSent}
                            className={`btn ${selectedRegistration.eventReminderSent ? 'btn-secondary opacity-50' : 'btn-primary'}`}
                          >
                            {sendingReminder ? (
                              <span className="flex items-center gap-2">
                                <LoadingSpinner size="sm" />
                                Sending...
                              </span>
                            ) : selectedRegistration.eventReminderSent ? (
                              'Reminder Already Sent'
                            ) : (
                              'Send Event Reminder'
                            )}
                          </button>
                          {selectedRegistration.eventReminderSent && selectedRegistration.eventReminderSentAt && (
                            <span className="text-sm text-gray-500">
                              Sent {formatDate(selectedRegistration.eventReminderSentAt)}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {selectedRegistration.eventReminderSent
                            ? 'Each registrant can only receive one event reminder.'
                            : 'Send a reminder email about the upcoming event.'}
                        </p>
                      </>
                    )}
                  </div>
                )}

                {/* Refund Button - only for Stripe payments, not check */}
                {selectedRegistration.status === 'paid' && selectedRegistration.paymentMethod === 'sponsorship' && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-gray-500">
                      This registration is part of a sponsorship foursome. To modify, edit the golfer details on the Sponsors page.
                      Refunds must be processed through the sponsorship.
                    </p>
                  </div>
                )}
                {selectedRegistration.status === 'paid' && selectedRegistration.paymentMethod !== 'check' && selectedRegistration.paymentMethod !== 'sponsorship' && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-gray-500 mb-2">
                      A $5 processing fee per golfer registration will be deducted from the refund.
                    </p>
                    <button
                      onClick={() => handleRefund(selectedRegistration)}
                      disabled={processingRefund}
                      className="btn btn-danger"
                    >
                      {processingRefund ? (
                        <span className="flex items-center gap-2">
                          <LoadingSpinner size="sm" />
                          Processing...
                        </span>
                      ) : (
                        `Process Refund (${formatCurrency(getRefundAmount(selectedRegistration))})`
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Silent Auction Email Modal */}
      {auctionModalOpen && (
        <SilentAuctionModal
          golfers={auctionGolfers}
          loadingGolfers={loadingAuctionGolfers}
          onClose={() => setAuctionModalOpen(false)}
          variant={auctionVariant}
        />
      )}
    </div>
  )
}
